import type { CSSProperties } from 'react'

/**
 * The mockup draws every icon as a 24×24 stroked path — no icon font, no
 * filled glyphs. These are those paths verbatim, so the line weight stays
 * consistent with the design instead of mixing in MUI's filled icon set.
 */
export const ICON_PATHS = {
  refresh: ['M20 11a8 8 0 1 0-2.3 5.7', 'M20 4v7h-7'],
  search: ['M20 20l-4.3-4.3'],
  eye: ['M2 12s3.6-6.5 10-6.5S22 12 22 12s-3.6 6.5-10 6.5S2 12 2 12z'],
  trash: ['M4 7h16', 'M9 7V4.5h6V7', 'M6 7l1 13h10l1-13'],
  warning: ['M12 3l9.5 17H2.5L12 3z', 'M12 9.5v4.5', 'M12 17.4h.01'],
  check: ['M20 6L9 17l-5-5'],
  plus: ['M12 5v14M5 12h14'],
  upload: ['M12 16V4', 'M7 9l5-5 5 5', 'M4 16v3a1 1 0 0 0 1 1h14a1 1 0 0 0 1-1v-3'],
  download: ['M12 4v12', 'M7 11l5 5 5-5', 'M4 20h16'],
  external: [
    'M14 4h6v6',
    'M20 4l-8 8',
    'M18 14v5a1 1 0 0 1-1 1H5a1 1 0 0 1-1-1V7a1 1 0 0 1 1-1h5',
  ],
  sun: [
    'M12 4.5v-2M12 21.5v-2M4.5 12h-2M21.5 12h-2M6.4 6.4L5 5M19 19l-1.4-1.4M6.4 17.6L5 19M19 5l-1.4 1.4M12 7.5a4.5 4.5 0 1 0 0 9 4.5 4.5 0 0 0 0-9z',
  ],
  moon: ['M20 14.5A8.5 8.5 0 0 1 9.5 4a8.5 8.5 0 1 0 10.5 10.5z'],
  logout: ['M15 17l5-5-5-5', 'M20 12H9', 'M9 21H6a2 2 0 0 1-2-2V5a2 2 0 0 1 2-2h3'],
  settings: [
    'M19.4 15a1.65 1.65 0 0 0 .33 1.82l.06.06a2 2 0 0 1-2.83 2.83l-.06-.06a1.65 1.65 0 0 0-1.82-.33 1.65 1.65 0 0 0-1 1.51V21a2 2 0 0 1-4 0v-.09a1.65 1.65 0 0 0-1.08-1.51 1.65 1.65 0 0 0-1.82.33l-.06.06a2 2 0 0 1-2.83-2.83l.06-.06a1.65 1.65 0 0 0 .33-1.82 1.65 1.65 0 0 0-1.51-1H3a2 2 0 0 1 0-4h.09a1.65 1.65 0 0 0 1.51-1.08 1.65 1.65 0 0 0-.33-1.82l-.06-.06a2 2 0 0 1 2.83-2.83l.06.06a1.65 1.65 0 0 0 1.82.33H9a1.65 1.65 0 0 0 1-1.51V3a2 2 0 0 1 4 0v.09a1.65 1.65 0 0 0 1 1.51 1.65 1.65 0 0 0 1.82-.33l.06-.06a2 2 0 0 1 2.83 2.83l-.06.06a1.65 1.65 0 0 0-.33 1.82V9a1.65 1.65 0 0 0 1.51 1H21a2 2 0 0 1 0 4h-.09a1.65 1.65 0 0 0-1.51 1z',
  ],
  admin: ['M12 3l8 4v5c0 4.4-3.4 8.3-8 9-4.6-.7-8-4.6-8-9V7l8-4z', 'M9 12l2 2 4-4'],
  // KPI tile glyphs, straight from the mockup's kpiDefs
  clock: ['M12 7v5l3 2M12 3a9 9 0 1 0 0 18 9 9 0 0 0 0-18z'],
  calendar: ['M8 3v4M16 3v4M3 9h18M5 5h14a2 2 0 0 1 2 2v12a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2V7a2 2 0 0 1 2-2z'],
  list: ['M4 6h16M4 12h10M4 18h7'],
  bolt: ['M6 3h12l-2 8h3l-9 10 2-8H6l2-10z'],
  trend: ['M3 17l6-6 4 4 8-8'],
  book: ['M6 3h12v18l-3-2-3 2-3-2-3 2V3zM9 8h6M9 12h6'],
  building: ['M4 21V8l8-5 8 5v13M9 21v-6h6v6'],
  bank: ['M4 21V8l8-5 8 5v13'],
  box: ['M6 3h12v18H6z'],
  filter: ['M4 5h16l-6 7v6l-4 2v-8L4 5z'],
  invoice: ['M7 3h10v18H7z', 'M10 8h4M10 12h4M10 16h2'],
  mail: ['M3 6h18v12H3z', 'M3 7l9 6 9-6'],
} as const

export type IconName = keyof typeof ICON_PATHS

/** Icons that need a circle drawn alongside their paths. */
const CIRCLES: Partial<Record<IconName, { cx: number; cy: number; r: number }[]>> = {
  search: [{ cx: 11, cy: 11, r: 7 }],
  eye: [{ cx: 12, cy: 12, r: 2.6 }],
  settings: [{ cx: 12, cy: 12, r: 3 }],
}

export function Icon({
  name,
  size = 16,
  strokeWidth = 2,
  style,
}: {
  name: IconName
  size?: number
  strokeWidth?: number
  style?: CSSProperties
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      style={{ flexShrink: 0, ...style }}
      aria-hidden="true"
    >
      {ICON_PATHS[name].map((d) => (
        <path key={d} d={d} />
      ))}
      {CIRCLES[name]?.map((c) => (
        <circle key={`${c.cx}-${c.cy}`} cx={c.cx} cy={c.cy} r={c.r} />
      ))}
    </svg>
  )
}

/** A raw path, for icons supplied as data (the KPI tiles). */
export function RawIcon({
  path,
  size = 20,
  strokeWidth = 2,
}: {
  path: string
  size?: number
  strokeWidth?: number
}) {
  return (
    <svg
      width={size}
      height={size}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth={strokeWidth}
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <path d={path} />
    </svg>
  )
}
